import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import '../../styles/modal.css';

const DROPDOWN_WIDTH = 238;
const VIEWPORT_PADDING = 8;
const MAX_MENU_HEIGHT = 360;

const DropdownMenu = ({
  isOpen,
  onClose,
  items,
  triggerRef,
}) => {
  const dropdownRef = useRef(null);
  const [activeSubmenu, setActiveSubmenu] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        onClose();
        setActiveSubmenu(null);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose, triggerRef]);

  useEffect(() => {
    if (!isOpen) {
      setActiveSubmenu(null);
    }
  }, [isOpen]);

  const handleSubmenuClick = (itemLabel) => {
    setActiveSubmenu(activeSubmenu === itemLabel ? null : itemLabel);
  };

  if (!isOpen || !triggerRef.current) return null;

  const triggerRect = triggerRef.current.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let left = triggerRect.right - DROPDOWN_WIDTH;
  if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
  if (left + DROPDOWN_WIDTH > viewportWidth - VIEWPORT_PADDING) {
    left = viewportWidth - DROPDOWN_WIDTH - VIEWPORT_PADDING;
  }

  const spaceBelow = viewportHeight - triggerRect.bottom - VIEWPORT_PADDING;
  const spaceAbove = triggerRect.top - VIEWPORT_PADDING;
  const positionAbove = spaceBelow < 220 && spaceAbove > spaceBelow;
  const maxHeight = Math.max(
    160,
    Math.min(MAX_MENU_HEIGHT, positionAbove ? spaceAbove : spaceBelow)
  );

  const handleSubmenuItemClick = (subItem) => {
    if (subItem.disabled) {
      return;
    }
    if (subItem.onClick) {
      subItem.onClick();
    }
    setActiveSubmenu(null);
    onClose();
  };

  return ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      className={`action-dropdown ${positionAbove ? 'dropdown-above' : ''}`}
      style={{
        position: 'fixed',
        top: positionAbove ? 'auto' : `${triggerRect.bottom}px`,
        bottom: positionAbove
          ? `${viewportHeight - triggerRect.top}px`
          : 'auto',
        left: `${left}px`,
        zIndex: 1000,
        width: `${DROPDOWN_WIDTH}px`,
        maxHeight: `${maxHeight}px`,
        overflowY: 'auto',
      }}
    >
      {items.map((item, index) =>
        item.type === 'divider' ? (
          <div key={`divider-${index}`} className="action-dropdown-divider" />
        ) : item.submenu ? (
          <div key={item.label} className="action-dropdown-item-with-submenu">
            <button
              className={`action-dropdown-item ${item.className || ''} has-submenu`}
              disabled={item.disabled}
              onClick={(e) => {
                e.stopPropagation();
                handleSubmenuClick(item.label);
              }}
            >
              {item.icon && (
                <span className="dropdown-item-icon">{item.icon}</span>
              )}
              <span className="flex-1 text-left">{item.label}</span>
              <svg
                className={`submenu-arrow ${activeSubmenu === item.label ? 'open' : ''}`}
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
            {activeSubmenu === item.label && (
              <div className="action-submenu">
                {item.submenu.map((subItem) => (
                  <button
                    key={subItem.label}
                    className="action-submenu-item"
                    disabled={subItem.disabled}
                    title={subItem.title}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSubmenuItemClick(subItem);
                    }}
                  >
                    {subItem.icon && (
                      <span className="submenu-item-icon">{subItem.icon}</span>
                    )}
                    {subItem.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div
            key={`${item.label}-${index}`}
            className="action-dropdown-item-wrapper"
            title={item.disabled && item.title ? item.title : undefined}
          >
            <button
              className={`action-dropdown-item ${item.className || ''}`}
              onClick={(e) => {
                e.stopPropagation();
                if (item.disabled) return;
                item.onClick();
                onClose();
              }}
              disabled={item.disabled}
              title={!item.disabled ? item.title : undefined}
            >
              {item.icon && (
                <span className="dropdown-item-icon">{item.icon}</span>
              )}
              <span className="flex-1 text-left">{item.label}</span>
            </button>
          </div>
        )
      )}
    </div>,
    document.body
  );
};

export default DropdownMenu;
